import { cn } from "@/lib/utils";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

interface ServiceWorkerUpdateToastProps {
  isVisible: boolean;
  onReload: () => void;
}

export function ServiceWorkerUpdateToast({
  isVisible,
  onReload,
}: ServiceWorkerUpdateToastProps) {
  const [phase, setPhase] = useState<"updating" | "reloading">("updating");

  useEffect(() => {
    if (!isVisible) return;

    // Phase 1: Zeige "Aktualisierung verfügbar" für 1.5 Sekunden
    const updateTimer = setTimeout(() => {
      setPhase("reloading");
    }, 1500);

    // Phase 2: Nach weiteren 0.5 Sekunden → automatischer Reload
    const reloadTimer = setTimeout(() => {
      onReload();
    }, 2000);

    return () => {
      clearTimeout(updateTimer);
      clearTimeout(reloadTimer);
    };
  }, [isVisible, onReload]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2",
        "flex items-center gap-3 rounded-lg border bg-background/95 backdrop-blur-sm",
        "px-4 py-3 shadow-lg",
        "w-[calc(100%-2rem)] sm:w-auto sm:max-w-md",
        "animate-in slide-in-from-bottom-4 fade-in-0",
        "sm:bottom-6"
      )}
      role="status"
      aria-live="polite"
    >
      {phase === "updating" ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              Aktualisierung verfügbar
            </span>
            <span className="text-xs text-muted-foreground">
              Wird im Hintergrund aktualisiert…
            </span>
          </div>
        </>
      ) : (
        <>
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              Aktualisierung abgeschlossen
            </span>
            <span className="text-xs text-muted-foreground">
              Seite wird neu geladen…
            </span>
          </div>
        </>
      )}
    </div>
  );
}
